import { supabase } from './supabase';

export interface ServiceModifiers {
  id: string;
  business_id: string;
  service_id: string;
  title_tag_enabled: boolean;
  title_tag_text: string | null;
  service_meter_enabled: boolean;
  meter_title: string | null;
  meter_rating: number | null;
  created_at: string;
  updated_at: string;
}

// Keep the old interface for backward compatibility
export interface ServiceTitleTag extends ServiceModifiers {}

export interface CreateServiceModifiersParams {
  business_id: string;
  service_id: string;
  title_tag_enabled: boolean;
  title_tag_text?: string | null;
  service_meter_enabled: boolean;
  meter_title?: string | null;
  meter_rating?: number | null;
}

export interface UpdateServiceModifiersParams {
  title_tag_enabled: boolean;
  title_tag_text?: string | null;
  service_meter_enabled: boolean;
  meter_title?: string | null;
  meter_rating?: number | null;
}

// Keep old interfaces for backward compatibility
export interface CreateServiceTitleTagParams extends CreateServiceModifiersParams {}
export interface UpdateServiceTitleTagParams extends UpdateServiceModifiersParams {}

/**
 * Get modifiers for a specific service
 */
export async function getServiceModifiers(
  serviceId: string
): Promise<ServiceModifiers | null> {
  const { data, error } = await supabase
    .from('service_title_tags')
    .select('*')
    .eq('service_id', serviceId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned - this is expected for services without modifiers
      return null;
    }
    throw error;
  }

  return data;
}

/**
 * Get title tag for a specific service (backward compatibility)
 */
export async function getServiceTitleTag(
  serviceId: string
): Promise<ServiceTitleTag | null> {
  return getServiceModifiers(serviceId);
}

/**
 * Create or update service modifiers
 */
export async function upsertServiceModifiers(
  serviceId: string,
  params: CreateServiceModifiersParams
): Promise<ServiceModifiers> {
  const { data, error } = await supabase
    .from('service_title_tags')
    .upsert({
      service_id: serviceId,
      ...params
    }, {
      onConflict: 'service_id'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Create or update title tag for a service (backward compatibility)
 */
export async function upsertServiceTitleTag(
  serviceId: string,
  params: CreateServiceTitleTagParams
): Promise<ServiceTitleTag> {
  return upsertServiceModifiers(serviceId, params);
}

/**
 * Update existing service modifiers
 */
export async function updateServiceModifiers(
  id: string,
  params: UpdateServiceModifiersParams
): Promise<ServiceModifiers> {
  const { data, error } = await supabase
    .from('service_title_tags')
    .update(params)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update existing title tag (backward compatibility)
 */
export async function updateServiceTitleTag(
  id: string,
  params: UpdateServiceTitleTagParams
): Promise<ServiceTitleTag> {
  return updateServiceModifiers(id, params);
}

/**
 * Delete title tag
 */
export async function deleteServiceTitleTag(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_title_tags')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Get all title tags for a business
 */
export async function getBusinessTitleTags(businessId: string): Promise<ServiceTitleTag[]> {
  const { data, error } = await supabase
    .from('service_title_tags')
    .select('*')
    .eq('business_id', businessId)
    .eq('title_tag_enabled', true);

  if (error) throw error;
  return data || [];
}

/**
 * Get service modifiers for multiple services (for customer-facing display)
 */
export async function getServicesTitleTags(
  serviceIds: string[]
): Promise<ServiceTitleTag[]> {
  if (serviceIds.length === 0) return [];

  const { data, error } = await supabase
    .from('service_title_tags')
    .select('*')
    .in('service_id', serviceIds)
    .or('title_tag_enabled.eq.true,service_meter_enabled.eq.true');

  if (error) throw error;
  return data || [];
}
